import type { ComponentDiagnostic } from "@swot/base-components";
import type { CmtsBackendEndpoint, CmtsBackendEndpointKey } from "../api/cmtsBackendEndpointRegistry.js";
import { CMTS_COMPLETE_BACKEND_ENDPOINT_BY_KEY, CMTS_COMPLETE_BACKEND_ENDPOINTS } from "../api/cmtsCompleteEndpointRegistry.js";
import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { getMissingCmtsPathParameters, resolveCmtsPathTemplate, type CmtsPathParameters } from "../api/cmtsPathTemplate.js";
import { repositoryCall, type RepositoryResult } from "./repositoryResult.js";

export type CmtsEndpointExecutionOptions = {
  pathParams?: CmtsPathParameters | undefined;
  query?: Record<string, string | number | boolean | null | undefined> | undefined;
  body?: unknown | undefined;
  formData?: FormData | undefined;
  idempotencyKey?: string | undefined;
  signal?: AbortSignal | undefined;
};

export type CmtsEndpointExecutionResult<T = unknown> = {
  endpoint: CmtsBackendEndpoint;
  path: string;
  method: string;
  data: T;
  diagnostics: readonly ComponentDiagnostic[];
};

export type BackendEndpointRepository = {
  inventory(): readonly CmtsBackendEndpoint[];
  byController(controllerGroup: string): readonly CmtsBackendEndpoint[];
  getEndpoint(key: CmtsBackendEndpointKey | string): CmtsBackendEndpoint | undefined;
  execute<T = unknown>(key: CmtsBackendEndpointKey | string, options?: CmtsEndpointExecutionOptions): Promise<RepositoryResult<CmtsEndpointExecutionResult<T>>>;
  readiness(tenantId?: string): Promise<RepositoryResult<readonly Record<string, unknown>[]>>;
  probe(id: string, payload?: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<Record<string, unknown>>>;
  probeModule(payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<Record<string, unknown>>>;
  updateStatus(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<Record<string, unknown>>>;
};

export function createBackendEndpointRepository(client: CmtsHttpClient): BackendEndpointRepository {
  return {
    inventory: () => CMTS_COMPLETE_BACKEND_ENDPOINTS,
    byController: (controllerGroup) => CMTS_COMPLETE_BACKEND_ENDPOINTS.filter((endpoint) => endpoint.controllerGroup === controllerGroup),
    getEndpoint: (key) => CMTS_COMPLETE_BACKEND_ENDPOINT_BY_KEY[key],
    readiness: (tenantId?: string) => repositoryCall(() => client.get<readonly Record<string, unknown>[]>(CMTS_API.operations.backendEndpoints, { query: { tenantId } }), "CMTS_ENDPOINT_READINESS"),
    probe: (id: string, payload?: Record<string, unknown>, tenantId?: string) => repositoryCall(() => client.post<Record<string, unknown>>(CMTS_API.operations.probeEndpoint(id), payload ?? {}, { query: { tenantId } }), "CMTS_ENDPOINT_PROBE"),
    probeModule: (payload: Record<string, unknown>, tenantId?: string) => repositoryCall(() => client.post<Record<string, unknown>>(CMTS_API.operations.probeModule, payload, { query: { tenantId } }), "CMTS_ENDPOINT_MODULE_PROBE"),
    updateStatus: (id: string, payload: Record<string, unknown>, tenantId?: string) => repositoryCall(() => client.patch<Record<string, unknown>>(CMTS_API.operations.updateEndpointStatus(id), payload, { query: { tenantId } }), "CMTS_ENDPOINT_STATUS_UPDATE"),
    execute: async <T = unknown>(key: CmtsBackendEndpointKey | string, options?: CmtsEndpointExecutionOptions): Promise<RepositoryResult<CmtsEndpointExecutionResult<T>>> => repositoryCall(async () => {
      const endpoint = CMTS_COMPLETE_BACKEND_ENDPOINT_BY_KEY[key];
      if (!endpoint) {
        throw { success: false, message: `CMTS endpoint key '${key}' is not registered.`, errors: [{ code: "CMTS_ENDPOINT_NOT_REGISTERED", field: "endpointKey", message: key }] };
      }
      const missing = getMissingCmtsPathParameters(endpoint.path, options?.pathParams);
      if (missing.length > 0) {
        throw { success: false, message: "CMTS endpoint path parameters are missing.", errors: missing.map((name) => ({ code: "CMTS_MISSING_PATH_PARAMETER", field: name, message: `${name} is required for ${endpoint.path}` })) };
      }
      const path = resolveCmtsPathTemplate(endpoint.path, options?.pathParams);
      const requestOptions = { query: options?.query, idempotencyKey: options?.idempotencyKey, signal: options?.signal };
      let data: T;
      switch (endpoint.method) {
        case "GET":
          data = await client.get<T>(path, requestOptions);
          break;
        case "POST":
          data = options?.formData ? await client.upload<T>(path, options.formData, requestOptions) : await client.post<T>(path, options?.body, requestOptions);
          break;
        case "PUT":
          data = await client.put<T>(path, options?.body, requestOptions);
          break;
        case "PATCH":
          data = await client.patch<T>(path, options?.body, requestOptions);
          break;
        case "DELETE":
          data = await client.delete<T>(path, requestOptions);
          break;
        default:
          throw { success: false, message: `Unsupported CMTS method '${String(endpoint.method)}'.`, errors: [{ code: "CMTS_UNSUPPORTED_METHOD", field: "method", message: String(endpoint.method) }] };
      }
      return { endpoint, path, method: endpoint.method, data, diagnostics: [] };
    }, `CMTS_ENDPOINT_${String(key).toUpperCase()}`),
  };
}
